import { faArrowsLeftRight } from "@fortawesome/pro-solid-svg-icons";
import { Html, useCursor } from "@react-three/drei";
import { type Vector3, useThree } from "@react-three/fiber";
import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import type { Mesh } from "three";
import { match } from "ts-pattern";
import { useCabinetStore } from "../../../../context/StoreContext";
import { getDimensions } from "../../../../hooks/getDimensions";
import { useCanvasStore } from "../../../../hooks/useCanvasStore";
import { useDrag } from "../../context/DragContext";
import {
	type CompartmentType,
	type HorizontalSide,
	type VerticalSide,
	getClosestSnappingPoint,
	getCompartmentsOnSide,
} from "../../data/Compartment";
import { type Horizontal, getNextHorizontal } from "../../data/Horizontal";
import { type Vertical, getNextVertical } from "../../data/Vertical";
import {
	BoundsLine,
	DragIcon,
	DragMesh,
	SizeDifferencePill,
	calculateDifference,
	getBoundsForCompartment,
	inverseSide,
} from "./Draggers";

export function CabinetDraggers() {
	const { state } = useCabinetStore();

	const [top, bottom] = useMemo(() => {
		const top: Vertical[] = [];
		const bottom: Vertical[] = [];

		// biome-ignore lint/complexity/noForEach: Type not set correctly when using for loop
		state.verticals.forEach((vertical) => {
			if (vertical.x === 0 || vertical.x + state.thickness === state.width) return;

			if (vertical.y === state.thickness) {
				bottom.push(vertical);
			}

			if (vertical.y + vertical.width + state.thickness === state.height) {
				top.push(vertical);
			}
		});

		return [top, bottom];
	}, [state.verticals, state.thickness, state.height, state.width]);

	const [left, right] = useMemo(() => {
		const left: Horizontal[] = [];
		const right: Horizontal[] = [];

		// biome-ignore lint/complexity/noForEach: Type not set correctly when using for loop
		state.horizontals.forEach((horizontal) => {
			if (horizontal.y === 0 || horizontal.y === state.height - state.thickness) return;

			if (horizontal.x === 0) {
				left.push(horizontal);
			}

			if (horizontal.x === state.width - horizontal.width) {
				right.push(horizontal);
			}
		});

		return [left, right];
	}, [state.horizontals, state.thickness, state.height, state.width]);

	const MARGIN = 0.06;

	return (
		<Fragment>
			{left.map((horizontal, key) => (
				<Dragger
					key={key}
					direction="right"
					horizontal={horizontal}
					position={[
						(horizontal.x - state.thickness) / 1000 - MARGIN,
						(horizontal.y + state.thickness / 2) / 1000,
						(horizontal.z + horizontal.depth) / 1000,
					]}
				/>
			))}

			{right.map((horizontal, key) => (
				<Dragger
					key={key}
					direction="left"
					horizontal={horizontal}
					position={[
						(horizontal.x + horizontal.width + state.thickness) / 1000 + MARGIN,
						(horizontal.y + state.thickness / 2) / 1000,
						(horizontal.z + horizontal.depth) / 1000,
					]}
				/>
			))}

			{top.map((vertical, key) => (
				<Dragger
					key={key}
					direction="bottom"
					vertical={vertical}
					position={[
						(vertical.x + state.thickness / 2) / 1000,
						(vertical.y + vertical.width + state.thickness) / 1000 + MARGIN,
						(vertical.z + vertical.depth) / 1000,
					]}
				/>
			))}

			{bottom.map((vertical, key) => (
				<Dragger
					key={key}
					direction="top"
					vertical={vertical}
					position={[
						(vertical.x + state.thickness / 2) / 1000,
						(vertical.y - state.thickness) / 1000 - MARGIN,
						(vertical.z + vertical.depth) / 1000,
					]}
				/>
			))}
		</Fragment>
	);
}

type DraggerProps = {
	position: Vector3;
} & (
	| {
			vertical: Vertical;
			direction: HorizontalSide;
			horizontal?: undefined;
	  }
	| {
			horizontal: Horizontal;
			direction: VerticalSide;
			vertical?: undefined;
	  }
);

function Dragger(props: DraggerProps) {
	const { state, api, updateCompartmentWidth, updateCompartmentHeight } = useCabinetStore();
	const { fullScreen } = useCanvasStore();
	const isVertical = props.vertical !== undefined;

	// the panel next to the dragger icon
	const panel = isVertical ? props.vertical : props.horizontal;

	const getAttachedVerticals = useCallback(() => {
		const verticals: Vertical[] = [];
		let nextVertical: Vertical | undefined = panel;

		while (nextVertical) {
			verticals.push(nextVertical);

			nextVertical = getNextVertical(nextVertical, props.direction as HorizontalSide, state);
		}

		return verticals;
	}, [panel, props.direction, state]);

	const getAttachedHorizontals = useCallback(() => {
		const horizontals: Horizontal[] = [];
		let nextHorizontal: Horizontal | undefined = panel;

		while (nextHorizontal) {
			horizontals.push(nextHorizontal);

			nextHorizontal = getNextHorizontal(nextHorizontal, props.direction as VerticalSide, state);
		}

		return horizontals;
	}, [panel, props.direction, state]);

	const panels = useMemo(
		() =>
			match(isVertical)
				.with(true, () => getAttachedVerticals())
				.with(false, () => getAttachedHorizontals())
				.exhaustive(),
		[getAttachedHorizontals, getAttachedVerticals, isVertical],
	);

	const ref = useRef<Mesh>(null);

	const [affected, setAffected] = useState<CompartmentType[]>([]);
	const [difference, setDifference] = useState<number>();

	const { viewport, invalidate } = useThree((selector) => ({
		viewport: selector.viewport,
		invalidate: selector.invalidate,
	}));

	const width = useMemo(() => {
		const total = panels.reduce((acc, panel) => acc + panel.width, 0);

		if (isVertical) {
			return total + (panels.length - 1) * state.thickness;
		}

		return total;
	}, [isVertical, panels, state.thickness]);

	const dimensions = useMemo(() => {
		const smallestY = Math.min(...panels.map((panel) => panel.y));
		const smallestX = Math.min(...panels.map((panel) => panel.x));

		return match(isVertical)
			.with(true, () => ({
				...getDimensions(panel),
				x: (panel.x + panel.height / 2) / 1000,
				y: (smallestY + width / 2) / 1000,
				width: width / 1000,
			}))
			.with(false, () => ({
				...getDimensions(panel),
				x: (smallestX + width / 2) / 1000,
				width: width / 1000,
			}))
			.exhaustive();
	}, [panel, panels, width, isVertical]);

	const compartments = useMemo(() => {
		const compartments = [...state.compartments.values()];

		const ids = match(isVertical)
			.with(true, () =>
				panels
					.map(
						(panel) => compartments.find((c) => c.x + c.width === panel.x && c.y === panel.y)?.id,
					)
					.filter(Boolean),
			)
			.with(false, () =>
				panels
					.map(
						(panel) =>
							compartments.find(
								(c) =>
									panel.y === c.y + c.height &&
									c.x >= panel.x &&
									c.x + c.width <= panel.x + panel.width,
							)?.id,
					)
					.filter(Boolean),
			)
			.exhaustive();

		return ids.map((id) => state.compartments.get(id)).filter(Boolean);
	}, [isVertical, panels, state.compartments]);

	const thickness = state.thickness / 1000;

	const bounds = useMemo(() => {
		const side = isVertical ? "left" : "bottom";

		const inverseCompartments = compartments
			.flatMap((compartment) => {
				return [...getCompartmentsOnSide(compartment, inverseSide(side), state).values()];
			})
			.map((compartment) => getBoundsForCompartment(compartment, side, state, api));

		const bounds = compartments.map((compartment) =>
			getBoundsForCompartment(compartment, inverseSide(side), state, api),
		);

		bounds.push(...inverseCompartments);

		if (bounds.length === 0) {
			return { top: 0, bottom: 0, left: 0, right: 0 };
		}

		return bounds.reduce((carry, next) => {
			if (!carry) return next;

			return {
				top: Math.min(carry.top, next.top) - thickness / 2,
				bottom: Math.min(carry.bottom, next.bottom) - thickness / 2,
				left: Math.min(carry.left, next.left) - thickness / 2,
				right: Math.min(carry.right, next.right) - thickness / 2,
			};
		});
	}, [compartments, isVertical, state, thickness, api]);

	const getXSnappingPoints = useCallback(
		(difference: number) => {
			const minY = Math.min(...panels.map((panel) => panel.y));
			const length =
				panels.reduce((acc, panel) => acc + panel.width, 0) + (panels.length - 1) * state.thickness;

			const points = Array.from(state.compartments.values())
				.filter(
					(c) =>
						minY + length + state.thickness === c.y || minY === c.y + c.height + state.thickness,
				)
				.flatMap((c) => [c.x, c.x + c.width + state.thickness]);

			const uniquePoints = [...new Set(points)];

			return getClosestSnappingPoint(uniquePoints, difference, state, api);
		},
		[panels, state, api],
	);

	const getYSnappingPoints = useCallback(
		(difference: number) => {
			const minX = Math.min(...panels.map((panel) => panel.x));
			const length = panels.reduce((acc, panel) => acc + panel.width, 0);

			const points = Array.from(state.compartments.values())
				.filter((c) => minX === c.x + c.width || minX + length === c.x + c.width + state.thickness)
				.flatMap((c) => [c.y, c.y + c.height + state.thickness]);

			const uniquePoints = [...new Set(points)];

			return getClosestSnappingPoint(uniquePoints, difference, state, api);
		},
		[panels, state, api],
	);

	const { bind, isDragging } = useDrag({
		onDragStart: () => {
			setAffected([...compartments]);
		},
		onDrag: ({ offset: [x, y] }) => {
			// update the position of the placeholder mesh
			match(isVertical)
				.with(true, () => {
					if (ref.current) ref.current.position.x = dimensions.x + x / viewport.factor;
				})
				.with(false, () => {
					if (ref.current) ref.current.position.y = dimensions.y - y / viewport.factor;
				})
				.exhaustive();

			if (ref.current) {
				// update the amount of the movement to use in the size indicators
				setDifference(calculateDifference(panel, ref.current, isVertical) - state.thickness);
			}

			invalidate();
		},
		onDragEnd: () => {
			if (ref.current) {
				const difference = calculateDifference(panel, ref.current, isVertical);

				const startPoint = isVertical ? panel.x : panel.y;

				const newSnap = isVertical
					? getXSnappingPoints(startPoint + difference) - state.thickness
					: getYSnappingPoints(startPoint + difference) - state.thickness;

				const amount = Math.round(newSnap - startPoint);

				match(isVertical)
					.with(true, () => {
						for (const compartment of compartments) {
							updateCompartmentWidth(compartment, "right", amount);
						}
					})
					.with(false, () => {
						for (const compartment of compartments) {
							updateCompartmentHeight(compartment, "top", amount);
						}
					})
					.exhaustive();
			}

			// when the drag has ended we want to stop showing the size indicators
			setDifference(undefined);
			setAffected([]);
		},
		config: {
			drag: {
				from: [panel.x / 1000, panel.y / 1000],
				bounds: {
					top: -bounds.top * viewport.factor,
					bottom: bounds.bottom * viewport.factor,
					left: -bounds.left * viewport.factor,
					right: bounds.right * viewport.factor,
				},
			},
		},
	});

	useCursor(isDragging, "grabbing");

	if (panels.length <= 1 || fullScreen) return null;

	return (
		<Fragment>
			<Html center position={props.position}>
				<DragIcon className="px-1" icon={faArrowsLeftRight} vertical={!isVertical} {...bind()} />
			</Html>

			{isDragging && (
				<Fragment>
					<DragMesh
						ref={ref}
						position={[dimensions.x, dimensions.y, dimensions.z + 0.02]}
						rotation={isVertical ? [0, 0, Math.PI / 2] : undefined}
						dimensions={[dimensions.width, dimensions.height, dimensions.depth]}
						isDragging={isDragging}
					/>

					<BoundsLine
						start={
							isVertical
								? [
										dimensions.x + bounds.right + thickness / 2,
										dimensions.y - width / 1000 / 2,
										dimensions.z + dimensions.depth / 2,
									]
								: [
										dimensions.x - width / 1000 / 2,
										dimensions.y - bounds.bottom - thickness / 2,
										dimensions.z + dimensions.depth / 2,
									]
						}
						end={
							isVertical
								? [
										dimensions.x + bounds.right + thickness / 2,
										dimensions.y + width / 1000 / 2,
										dimensions.z + dimensions.depth / 2,
									]
								: [
										dimensions.x + width / 1000 / 2,
										dimensions.y - bounds.bottom - thickness / 2,
										dimensions.z + dimensions.depth / 2,
									]
						}
					/>

					<BoundsLine
						start={
							isVertical
								? [
										dimensions.x - bounds.left - thickness / 2,
										dimensions.y + width / 1000 / 2,
										dimensions.z + dimensions.depth / 2,
									]
								: [
										dimensions.x + width / 1000 / 2,
										dimensions.y + bounds.top + thickness / 2,
										dimensions.z + dimensions.depth / 2,
									]
						}
						end={
							isVertical
								? [
										dimensions.x - bounds.left - thickness / 2,
										dimensions.y - width / 1000 / 2,
										dimensions.z + dimensions.depth / 2,
									]
								: [
										dimensions.x - width / 1000 / 2,
										dimensions.y + bounds.top + thickness / 2,
										dimensions.z + dimensions.depth / 2,
									]
						}
					/>

					{affected.map((compartment) => (
						<SizeDifferencePill
							side={isVertical ? "left" : "bottom"}
							compartment={compartment}
							difference={difference ?? 0}
							key={compartment.id}
						/>
					))}
				</Fragment>
			)}
		</Fragment>
	);
}
